import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import { useClientInfo } from './ClientInfoContext';
import {
  ControlTag,
  TwoWayService,
} from '../components/relay-messenger-axios-service/relay-messenger-axios-service';

interface AgentTagsContextType {
  agentTags: ControlTag[];
  setAgentTags: Dispatch<SetStateAction<ControlTag[]>>;
  fetchAgentTags: () => Promise<void>;
  isAgentTagsLoading: boolean;
  hasLoadedAgentTags: boolean;
}

const defaultState: AgentTagsContextType = {
  agentTags: [],
  setAgentTags: () => {},
  fetchAgentTags: async () => {},
  isAgentTagsLoading: false,
  hasLoadedAgentTags: false,
};

const AgentTagsContext = createContext<AgentTagsContextType>(defaultState);

export const useAgentTags = () => useContext(AgentTagsContext);

interface ProviderProps {
  children: ReactNode;
}

export const AgentTagsProvider = ({ children }: ProviderProps) => {
  const [{ clientId }] = useClientInfo();
  const [agentTags, setAgentTags] = useState<ControlTag[]>([]);
  const [isAgentTagsLoading, setIsAgentTagsLoading] = useState(false);
  const [hasLoadedAgentTags, setHasLoadedAgentTags] = useState(false);

  const fetchAgentTags = useCallback(async () => {
    try {
      if (!clientId || isAgentTagsLoading) {
        return;
      }

      setIsAgentTagsLoading(true);
      const url = `/client/${clientId}/agent/tags`;
      const allAgentTags = await TwoWayService.getAgentTags(url);
      setAgentTags(allAgentTags);
    } catch (error) {
      console.error('Failed to fetch agent tags:', error);
    } finally {
      setIsAgentTagsLoading(false);
      setHasLoadedAgentTags(true);
    }
  }, [clientId, isAgentTagsLoading]);

  return (
    <AgentTagsContext.Provider
      value={{
        agentTags,
        setAgentTags,
        fetchAgentTags,
        isAgentTagsLoading,
        hasLoadedAgentTags,
      }}
    >
      {children}
    </AgentTagsContext.Provider>
  );
};
